import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { ArrowRight, ArrowLeft, Check, AlertTriangle, ChevronDown, ChevronUp, Send, Loader2, Building2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { PrivacyNotice } from "./PrivacyNotice";
import {
  PB_HERO, PB_STATS, PB_PROBLEM, PB_WHY_TRUST, PB_BILLING_ENGINE,
  PB_BRIDGE, PB_COMPLIANCE, PB_CASHFLOW, PB_FEATURES,
  PB_CUSTOMERS, PB_AUDIENCE, PB_FAQ
} from "../data/patientBillingData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ── Reusable Section Wrapper ── */
const Section = ({ id, bg = "bg-white", children }) => (
  <section id={id} className={`py-20 lg:py-28 ${bg}`}>
    <div className="max-w-7xl mx-auto px-6">{children}</div>
  </section>
);

/* ── Hero ── */
const PBHero = () => (
  <section className="relative min-h-[85vh] flex flex-col justify-center bg-[#465155] overflow-hidden">
    <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#308795]/10 rounded-full -translate-y-1/3 translate-x-1/3" />
    <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#E95D54]/[0.06] rounded-full translate-y-1/3 -translate-x-1/4" />

    <div className="relative max-w-7xl mx-auto px-6 pt-32 pb-16 lg:pt-40 lg:pb-24 w-full">
      <div className="flex items-center justify-between mb-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#A9B294] hover:text-[#E5BD77] transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>
        <div className="inline-flex items-center gap-2 bg-[#E95D54]/15 text-[#FBBC9F] text-sm font-semibold px-4 py-1.5 rounded-full">
          <span className="w-2 h-2 bg-[#E95D54] rounded-full animate-pulse" />
          {PB_HERO.eyebrow}
        </div>
      </div>

      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight mb-5 max-w-4xl">
        {PB_HERO.headline}
      </h1>

      <p className="text-lg lg:text-xl text-[#D5CFC9] leading-relaxed max-w-2xl mb-10">
        {PB_HERO.subheadline}
      </p>

      <div className="flex flex-col sm:flex-row gap-4 mb-16">
        <a href={PB_HERO.primaryCTA.href}>
          <Button className="bg-[#E95D54] hover:bg-[#d44d44] text-white font-semibold px-8 py-6 text-base rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-[#E95D54]/20 hover:-translate-y-0.5 group">
            {PB_HERO.primaryCTA.label}
            <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Button>
        </a>
        <a href={PB_HERO.secondaryCTA.href}>
          <Button variant="outline" className="border-2 border-white/20 text-white hover:border-[#E5BD77] hover:text-[#E5BD77] font-semibold px-8 py-6 text-base rounded-xl transition-all duration-300">
            {PB_HERO.secondaryCTA.label}
          </Button>
        </a>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {PB_STATS.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} className="bg-white/[0.06] backdrop-blur-sm border border-white/[0.08] rounded-xl px-5 py-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#E5BD77]/15 flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5 text-[#E5BD77]" />
              </div>
              <div>
                <div className="text-lg font-bold text-white">{s.value}</div>
                <div className="text-xs text-[#A9B294] font-medium">{s.label}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ── Problem ── */
const PBProblem = () => (
  <Section bg="bg-[#FAFAF7]">
    <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
      <div>
        <span className="text-sm font-bold text-[#E95D54] uppercase tracking-widest mb-3 block">The Challenge</span>
        <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-4">{PB_PROBLEM.headline}</h2>
        <p className="text-[#6B6567] text-lg leading-relaxed mb-6">{PB_PROBLEM.description}</p>
        <p className="text-[#494546] font-medium text-sm leading-relaxed">{PB_PROBLEM.conclusion}</p>
      </div>
      <div className="mt-10 lg:mt-0">
        <div className="bg-white rounded-2xl border border-[#E0D1BB]/30 p-8">
          <h3 className="text-sm font-bold text-[#308795] uppercase tracking-widest mb-5">The Financial Matrix Behind Every Patient</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {PB_PROBLEM.factors.map((f, i) => (
              <div key={i} className="flex items-start gap-3">
                <AlertTriangle className="w-4 h-4 mt-0.5 text-[#E5BD77] shrink-0" />
                <span className="text-sm text-[#494546]">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </Section>
);

/* ── Why Trust ── */
const PBWhyTrust = () => (
  <Section bg="bg-white">
    <div className="max-w-3xl">
      <span className="text-sm font-bold text-[#7EA13E] uppercase tracking-widest mb-3 block">Our Track Record</span>
      <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-3">{PB_WHY_TRUST.headline}</h2>
      <p className="text-[#308795] text-lg font-medium italic mb-4">"{PB_WHY_TRUST.subheadline}"</p>
      <p className="text-[#6B6567] text-base leading-relaxed mb-10">{PB_WHY_TRUST.description}</p>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {PB_WHY_TRUST.credentials.map((c, i) => {
        const Icon = c.icon;
        return (
          <div key={i} className="flex items-center gap-4 bg-[#FAFAF7] border border-[#E0D1BB]/30 rounded-xl px-5 py-4 transition-all duration-300 hover:shadow-md hover:border-[#308795]/20">
            <div className="w-10 h-10 rounded-lg bg-[#308795]/[0.08] flex items-center justify-center shrink-0">
              <Icon className="w-5 h-5 text-[#308795]" />
            </div>
            <span className="text-sm font-medium text-[#494546]">{c.text}</span>
          </div>
        );
      })}
    </div>
  </Section>
);

/* ── Billing Engine ── */
const PBBillingEngine = () => (
  <Section id="pb-features" bg="bg-[#FAFAF7]">
    <div className="lg:grid lg:grid-cols-5 lg:gap-16">
      <div className="lg:col-span-2">
        <span className="text-sm font-bold text-[#308795] uppercase tracking-widest mb-3 block">Core Capability</span>
        <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-3">{PB_BILLING_ENGINE.headline}</h2>
        <p className="text-[#E95D54] font-semibold text-lg mb-4">{PB_BILLING_ENGINE.subheadline}</p>
        <p className="text-[#6B6567] text-base leading-relaxed mb-6">{PB_BILLING_ENGINE.description}</p>
        <div className="bg-[#308795]/[0.06] border border-[#308795]/15 rounded-xl p-5">
          <p className="text-sm font-semibold text-[#308795]">{PB_BILLING_ENGINE.outcome}</p>
        </div>
      </div>
      <div className="lg:col-span-3 mt-10 lg:mt-0">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {PB_BILLING_ENGINE.capabilities.map((c, i) => {
            const Icon = c.icon;
            return (
              <div key={i} className="group bg-white border border-[#E0D1BB]/30 rounded-xl p-5 transition-all duration-300 hover:shadow-md hover:border-[#308795]/20 hover:-translate-y-0.5">
                <div className="w-10 h-10 rounded-lg bg-[#E5BD77]/12 flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5 text-[#A56A3E]" />
                </div>
                <span className="text-sm font-medium text-[#2C2A2B]">{c.text}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  </Section>
);

/* ── Bridge ── */
const PBBridge = () => (
  <Section bg="bg-[#465155]">
    <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
      <div>
        <span className="text-sm font-bold text-[#E5BD77] uppercase tracking-widest mb-3 block">Integration</span>
        <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight mb-4">{PB_BRIDGE.headline}</h2>
        <p className="text-[#D5CFC9] text-base leading-relaxed mb-6">{PB_BRIDGE.description}</p>
        <p className="text-[#E5BD77] font-semibold text-base italic">"{PB_BRIDGE.outcome}"</p>
      </div>
      <div className="mt-10 lg:mt-0">
        <div className="space-y-3">
          {PB_BRIDGE.functions.map((f, i) => (
            <div key={i} className="flex items-start gap-4 bg-white/[0.06] border border-white/[0.08] rounded-xl px-5 py-4">
              <Check className="w-5 h-5 text-[#7EA13E] mt-0.5 shrink-0" />
              <span className="text-sm text-[#D5CFC9]">{f}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </Section>
);

/* ── Compliance ── */
const PBCompliance = () => (
  <Section bg="bg-white">
    <div className="max-w-3xl mx-auto text-center mb-12">
      <span className="text-sm font-bold text-[#E95D54] uppercase tracking-widest mb-3 block">Compliance</span>
      <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-4">{PB_COMPLIANCE.headline}</h2>
      <p className="text-[#6B6567] text-lg leading-relaxed">{PB_COMPLIANCE.description}</p>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {PB_COMPLIANCE.outputs.map((o, i) => {
        const Icon = o.icon;
        return (
          <div key={i} className="bg-[#FAFAF7] border border-[#E0D1BB]/30 rounded-2xl p-6 text-center transition-all duration-300 hover:shadow-md hover:-translate-y-0.5">
            <div className="w-14 h-14 rounded-xl bg-[#308795]/[0.08] flex items-center justify-center mx-auto mb-4">
              <Icon className="w-7 h-7 text-[#308795]" />
            </div>
            <p className="text-sm font-semibold text-[#2C2A2B]">{o.text}</p>
          </div>
        );
      })}
    </div>
  </Section>
);

/* ── Cash Flow ── */
const PBCashFlow = () => (
  <Section bg="bg-[#FAFAF7]">
    <div className="max-w-3xl">
      <span className="text-sm font-bold text-[#7EA13E] uppercase tracking-widest mb-3 block">Financial Impact</span>
      <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-4">{PB_CASHFLOW.headline}</h2>
      <p className="text-[#6B6567] text-lg leading-relaxed mb-8">{PB_CASHFLOW.description}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {PB_CASHFLOW.benefits.map((b, i) => (
          <div key={i} className="flex items-center gap-3 bg-white border border-[#E0D1BB]/30 rounded-xl px-5 py-4">
            <Check className="w-5 h-5 text-[#7EA13E] shrink-0" />
            <span className="text-sm font-medium text-[#494546]">{b}</span>
          </div>
        ))}
      </div>
    </div>
  </Section>
);

/* ── Complete Features ── */
const PBFeatureGrid = () => (
  <Section bg="bg-white">
    <div className="text-center max-w-2xl mx-auto mb-14">
      <span className="text-sm font-bold text-[#308795] uppercase tracking-widest mb-3 block">Platform Capabilities</span>
      <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-4">Complete Feature Overview</h2>
      <p className="text-[#6B6567] text-lg leading-relaxed">Healthcare finance engineered for Singapore — and adaptable for regional markets across Asia Pacific.</p>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
      {PB_FEATURES.map((f, i) => {
        const Icon = f.icon;
        return (
          <div key={i} className="group bg-[#FAFAF7] border border-[#E0D1BB]/30 rounded-2xl p-5 text-center transition-all duration-300 hover:shadow-md hover:border-[#308795]/20 hover:-translate-y-0.5">
            <div className="w-12 h-12 rounded-xl bg-[#E5BD77]/12 flex items-center justify-center mx-auto mb-3">
              <Icon className="w-6 h-6 text-[#A56A3E]" />
            </div>
            <p className="text-xs font-semibold text-[#2C2A2B] leading-snug">{f.title}</p>
          </div>
        );
      })}
    </div>
  </Section>
);

/* ── Trusted Customers ── */
const PBCustomers = () => (
  <Section bg="bg-[#FAFAF7]">
    <div className="text-center max-w-2xl mx-auto mb-12">
      <span className="text-sm font-bold text-[#E95D54] uppercase tracking-widest mb-3 block">Trusted By</span>
      <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-4">
        Serving Singapore's Leading Care Institutions
      </h2>
      <p className="text-[#6B6567] text-lg">Trusted by healthcare finance teams across nursing homes and LTC providers.</p>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
      {PB_CUSTOMERS.map((customer, i) => (
        <div key={i} className="bg-white border border-[#E0D1BB]/30 rounded-xl p-5 flex flex-col items-center justify-center text-center transition-all duration-300 hover:shadow-md hover:border-[#308795]/20 min-h-[160px]">
          {customer.logo ? (
            <img
              src={customer.logo}
              alt={customer.name}
              className="w-20 h-20 object-contain mb-3"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-[#308795]/[0.08] flex items-center justify-center mb-3">
              <Building2 className="w-7 h-7 text-[#308795]" />
            </div>
          )}
          <p className="text-sm font-semibold text-[#2C2A2B] leading-snug">{customer.name}</p>
        </div>
      ))}
    </div>
  </Section>
);

/* ── FAQ (GEO Optimised) ── */
const PBFAQ = () => {
  const [open, setOpen] = useState(null);
  return (
    <Section bg="bg-white">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-sm font-bold text-[#308795] uppercase tracking-widest mb-3 block">Frequently Asked Questions</span>
          <h2 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight">Everything You Need to Know About CareOps 365</h2>
        </div>
        <div className="space-y-3">
          {PB_FAQ.map((item, i) => (
            <div key={i} className="border border-[#E0D1BB]/40 rounded-xl overflow-hidden transition-all duration-300">
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-[#FAFAF7] transition-colors"
              >
                <span className="text-base font-semibold text-[#2C2A2B] pr-4">{item.q}</span>
                {open === i ? <ChevronUp className="w-5 h-5 text-[#308795] shrink-0" /> : <ChevronDown className="w-5 h-5 text-[#D5CFC9] shrink-0" />}
              </button>
              {open === i && (
                <div className="px-6 pb-5 animate-fade-in">
                  <p className="text-sm text-[#6B6567] leading-relaxed">{item.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
};

/* ── Contact CTA ── */
const PBContact = () => {
  const [formData, setFormData] = useState({ firstName: "", lastName: "", email: "", company: "", website: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.email) {
      toast.error("Please fill in your first name, last name, and email.");
      return;
    }
    if (!privacyConsent) {
      toast.error("Please agree to the privacy policy to proceed.");
      return;
    }
    setLoading(true);
    try {
      const msgParts = [];
      if (formData.company) msgParts.push(`Company: ${formData.company}`);
      if (formData.website) msgParts.push(`Website: ${formData.website}`);
      if (formData.message) msgParts.push(formData.message);
      await axios.post(`${API}/enquiries`, {
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        message: msgParts.join("\n") || null,
        selected_path: "Patient Billing & Accounting",
      });
      setSubmitted(true);
      toast.success("Thank you! We'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section id="pb-contact" bg="bg-[#465155]">
      <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
        <div>
          <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight mb-4">
            Let's Strengthen the Financial Backbone of Care
          </h2>
          <p className="text-[#D5CFC9] text-lg leading-relaxed mb-6">
            CareOps 365 can help you simplify complexity, strengthen compliance, and future-proof your financial operations.
          </p>
          <p className="text-[#E5BD77] font-semibold italic text-base">"Empowering Care. Enhancing Trust. Enabling Impact."</p>

          <div className="mt-8">
            <h3 className="text-sm font-bold text-white/70 uppercase tracking-wider mb-4">Who Can Benefit</h3>
            <div className="flex flex-wrap gap-2">
              {PB_AUDIENCE.map((a, i) => (
                <span key={i} className="text-xs font-medium bg-white/[0.08] border border-white/[0.1] text-[#D5CFC9] px-3 py-1.5 rounded-lg">{a}</span>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-10 lg:mt-0">
          {!submitted ? (
            <div className="bg-white/[0.06] backdrop-blur-sm border border-white/[0.1] rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-6">Book a CareOps 365 Demo</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#D5CFC9] mb-1.5">First Name & Last Name</label>
                  <div className="grid grid-cols-2 gap-3"><input type="text" value={formData.firstName} onChange={(e) => setFormData({...formData, firstName: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#E5BD77] focus:ring-2 focus:ring-[#E5BD77]/10 transition-all" placeholder="First name" /><input type="text" value={formData.lastName} onChange={(e) => setFormData({...formData, lastName: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#E5BD77] focus:ring-2 focus:ring-[#E5BD77]/10 transition-all" placeholder="Last name" /></div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#D5CFC9] mb-1.5">Email</label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#E5BD77] focus:ring-2 focus:ring-[#E5BD77]/10 transition-all" placeholder="your@email.com" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-[#D5CFC9] mb-1.5">Name of Company</label><input type="text" value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#E5BD77] focus:ring-2 focus:ring-[#E5BD77]/10 transition-all" placeholder="Company name" /></div>
                  <div><label className="block text-sm font-medium text-[#D5CFC9] mb-1.5">Website</label><input type="url" value={formData.website} onChange={(e) => setFormData({...formData, website: e.target.value})} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#E5BD77] focus:ring-2 focus:ring-[#E5BD77]/10 transition-all" placeholder="https://www.example.com" /></div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#D5CFC9] mb-1.5">Message (Optional)</label>
                  <textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} rows={3} className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/[0.06] text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#E5BD77] focus:ring-2 focus:ring-[#E5BD77]/10 transition-all resize-none" placeholder="Tell us about your facility..." />
                </div>
                <PrivacyNotice checked={privacyConsent} onChange={setPrivacyConsent} dark />
                <Button type="submit" disabled={loading || !privacyConsent} className="w-full bg-[#E95D54] hover:bg-[#d44d44] text-white font-semibold py-6 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-[#E95D54]/20 text-base disabled:opacity-60">
                  {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</>) : (<>Book Demo<Send className="ml-2 h-4 w-4" /></>)}
                </Button>
              </form>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-[#7EA13E]/20 flex items-center justify-center mx-auto mb-5">
                <svg className="w-8 h-8 text-[#7EA13E]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Thank You!</h3>
              <p className="text-[#D5CFC9]">We'll be in touch to schedule your CareOps 365 demo.</p>
            </div>
          )}
        </div>
      </div>
    </Section>
  );
};

/* ── Main Page Component ── */
export default function PatientBillingPage() {
  return (
    <>
      <PBHero />
      <PBProblem />
      <PBWhyTrust />
      <PBBillingEngine />
      <PBBridge />
      <PBCompliance />
      <PBCashFlow />
      <PBFeatureGrid />
      <PBCustomers />
      <PBFAQ />
      <PBContact />
    </>
  );
}
